import { sb } from '../supabase';
import { eventToItem } from './consoleNotifications';

// /admin2/traders — every trader in one place (owner, 2026-10-11). Replaces the
// legacy /admin/traders, so an alert email or a conversation can open the
// trader without ever leaving the new console.
//
// A trader is a profile with role 'buyer' (the only buyer role in production;
// measured 62 rows on 2026-10-11). The admin reads every table used here
// through is_admin_user() in its SELECT policy.
//
// NO "deactivate" button, deliberately. The legacy page had one, and it does
// write profiles.status (admins may — guard_profile_sensitive_fields returns
// early for is_admin_user()). But nothing on the platform tests a TRADER's
// status: 'inactive' gates suppliers only. A button whose effect is nil reads
// like a control and is not one.

const nf = (v) => (v == null ? '' : String(v).trim());

export const traderName = (p) => nf(p && (p.company_name || p.full_name || p.email || p.phone)) || '—';

// Saudi-first wa.me number from whatever the trader typed. Empty when unusable.
export function waNumber(raw) {
  const d = nf(raw).replace(/[^\d]/g, '');
  if (!d) return '';
  if (d.startsWith('966')) return d;
  if (d.startsWith('00')) return d.slice(2);
  if (d.startsWith('05')) return '966' + d.slice(1);
  if (d.length === 9 && d.startsWith('5')) return '966' + d;
  return d;
}

const latest = (...isos) => isos.filter(Boolean).sort().pop() || null;

// List rows: profile + request count + open (unhandled) alerts + last activity.
export async function fetchTraders() {
  const [profiles, requests, events] = await Promise.all([
    sb.from('profiles')
      .select('id, full_name, company_name, email, phone, whatsapp, city, country, created_at, last_platform, last_platform_at')
      .eq('role', 'buyer').order('created_at', { ascending: false }),
    sb.from('requests').select('buyer_id, created_at'),
    sb.from('admin_events').select('actor_id, created_at, handled_at'),
  ]);
  if (profiles.error) throw profiles.error;

  const reqBy = {};
  (requests.data || []).forEach((r) => {
    const a = (reqBy[r.buyer_id] = reqBy[r.buyer_id] || { n: 0, last: null });
    a.n += 1; a.last = latest(a.last, r.created_at);
  });
  const evBy = {};
  (events.data || []).forEach((e) => {
    if (!e.actor_id) return;
    const a = (evBy[e.actor_id] = evBy[e.actor_id] || { open: 0, last: null });
    if (!e.handled_at) a.open += 1;
    a.last = latest(a.last, e.created_at);
  });

  return (profiles.data || []).map((p) => ({
    ...p,
    requestCount: (reqBy[p.id] || {}).n || 0,
    openAlerts: (evBy[p.id] || {}).open || 0,
    lastActivity: latest(p.last_platform_at, (reqBy[p.id] || {}).last, (evBy[p.id] || {}).last),
  }));
}

export async function fetchTraderDetail(id) {
  const [profile, requests, events, purchases, convos] = await Promise.all([
    sb.from('profiles')
      .select('id, role, full_name, company_name, email, phone, whatsapp, city, country, lang, created_at, last_platform, last_platform_at')
      .eq('id', id).maybeSingle(),
    sb.from('requests')
      .select('id, request_ref, title_ar, title_en, status, managed_status, request_kind, quantity, unit, created_at')
      .eq('buyer_id', id).order('created_at', { ascending: false }).limit(100),
    sb.from('admin_events')
      .select('id, kind, created_at, actor_id, request_id, factory_id, counterpart_id, summary, email_state, handled_at')
      .eq('actor_id', id).order('created_at', { ascending: false }).limit(100),
    sb.from('purchase_requests')
      .select('id, quantity, status, created_at, product_ref, supplier_id')
      .eq('buyer_id', id).order('created_at', { ascending: false }).limit(50),
    sb.rpc('admin_trader_conversations'),
  ]);
  if (profile.error) throw profile.error;
  if (!profile.data) return null;

  // Product names for the purchases, in one round trip.
  const productIds = [...new Set((purchases.data || []).map((p) => p.product_ref).filter(Boolean))];
  const products = productIds.length
    ? await sb.from('products').select('id, name_ar, name_en, name_zh').in('id', productIds)
    : { data: [] };
  const productName = Object.fromEntries((products.data || []).map((p) => [p.id, nf(p.name_ar) || nf(p.name_en) || nf(p.name_zh)]));

  return {
    profile: profile.data,
    requests: requests.data || [],
    events: (events.data || []).map(eventToItem).filter(Boolean),
    purchases: (purchases.data || []).map((p) => ({ ...p, productName: productName[p.product_ref] || '' })),
    conversations: (convos.data || []).filter((c) => c.trader_id === id),
  };
}
